import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import DoneRoundedIcon from '@mui/icons-material/DoneRounded';
import HourglassEmptyRoundedIcon from '@mui/icons-material/HourglassEmptyRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { DownloadRounded } from '@mui/icons-material';
import useAPI from '../../../../api';
import { download } from '../../../../api/utils';
import StatusChip from '../../../../Components/Chip/StatusChip';
import Information from '../../../PracticesPage/Components/Information';
import { formatListItemDate } from '../../../../Helpers/dates';
import StyledTooltip from '../../../../Components/StyledTooltip';

const classes = {
  root: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '2%',
    alignItems: 'center',
    maxHeight: '80px',
    padding: '12px 24px',
  },
};

const STATUS_OPTIONS = {
  completed: DoneRoundedIcon,
  error: CloseRoundedIcon,
  processing: HourglassEmptyRoundedIcon,
};
const STATUS_COLORS = {
  completed: '#34A853',
  error: '#b00020',
  processing: '#ff6700',
};

const LoadListItem = function LoadListItem({
  id,
  fileName,
  userFullName,
  isSuccessFull,
  isFileDeleted,
  completedOn = null,
  createdOn = null,
  sectionName = '',
  errors = '',
}) {
  const intl = useIntl();
  const t = (key) => intl.formatMessage({ id: key });
  const API = useAPI();
  const getStatus = () => {
    if (!completedOn) return 'processing';
    return isSuccessFull ? 'completed' : 'error';
  };

  const handleDownload = () => {
    download(fileName, API.downloadWaveFile(id, { silent: true }));
  };

  const errorsArray = errors ? errors.split(',') : null;
  return (
    <div id={`data-entry-${id}`} style={classes.root}>
      <HistoryRoundedIcon />
      <Information
        caption={t('SECTION')}
        value={sectionName}
        styles={{ root: { flex: '0 1 15%' } }}
      />
      <Information caption={t('FILE_NAME')} value={fileName} />
      <Information
        caption={t('LOADED_ON')}
        value={formatListItemDate(createdOn, true)}
        styles={{ root: { flex: '0 1 15%' } }}
      />
      <Information
        caption={t('COMPLETED_ON')}
        value={formatListItemDate(completedOn, true)}
        styles={{ root: { flex: '0 1 15%' } }}
      />
      <Information caption={t('LOADED_BY')} value={userFullName} />
      <StyledTooltip
        placement="left"
        title={
          errorsArray?.length ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <Typography variant="h6">{t('ERRORS_LIST')}</Typography>
              {errorsArray?.map((el) => (
                <Typography key={el} variant="body1" color="error">
                  {intl.messages[el] ?? el}
                </Typography>
              ))}
            </Box>
          ) : (
            ''
          )
        }
      >
        <Box
          sx={{
            width: '130px',
            cursor: errorsArray?.length > 0 && 'pointer',
          }}
        >
          <StatusChip
            status={getStatus()}
            colors={STATUS_COLORS}
            options={STATUS_OPTIONS}
          />
        </Box>
      </StyledTooltip>

      <Tooltip title={intl.messages.DOWNLOAD}>
        <IconButton
          id={`download-${fileName}`}
          onClick={handleDownload}
          disabled={isFileDeleted}
        >
          <DownloadRounded />
        </IconButton>
      </Tooltip>
    </div>
  );
};

LoadListItem.propTypes = {
  id: PropTypes.string.isRequired,
  fileName: PropTypes.string.isRequired,
  userFullName: PropTypes.string.isRequired,
  isSuccessFull: PropTypes.bool,
  completedOn: PropTypes.object,
  sectionName: PropTypes.string,
  errors: PropTypes.string,
  createdOn: PropTypes.object,
  isFileDeleted: PropTypes.bool,
};

export default LoadListItem;
